import { FC, useEffect, useMemo } from 'react';
import type { ControllerRenderProps, FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from '@hub/helpers/use-store';
import { getCountries } from '@monorepo/client-common';
import { Autocomplete, Box, Typography, useTheme } from '@monorepo/nova-ui';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import { RegisterRequest } from '@monorepo/types';
import { FlagOption } from './flag-option';

type Props = {
  field: ControllerRenderProps<RegisterRequest, 'country'>;
  error?: FieldError;
};

export const FormCountrySelectNova: FC<Props> = ({ field, error }) => {
  const {
    dataStore: {
      applicationConfigStore: { applicationConfig, initCountryFilter },
    },
  } = useStore();
  const { palette } = useTheme();
  const { t } = useTranslation(['common', 'errors']);
  const { isLoading, isLoadError, updateLoadingState } = useLoading();
  const { countryFilter } = applicationConfig;

  const countries = useMemo(() => getCountries(countryFilter), [countryFilter]);

  useEffect(() => {
    const init = async () => {
      if (countryFilter) return;

      try {
        updateLoadingState(LoadingState.Loading);

        await initCountryFilter();

        updateLoadingState(LoadingState.Loaded);
      } catch {
        updateLoadingState(LoadingState.Error);
      }
    };

    init();
  }, [countryFilter]);

  return (
    <Box>
      <Autocomplete
        options={countries}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.text
        }
        value={
          countries.find((country) => country.value === field.value) || null
        }
        onChange={(_event, newValue) => {
          if (newValue && typeof newValue === 'object' && 'value' in newValue) {
            field.onChange(newValue.value);
          } else {
            field.onChange('');
          }
        }}
        isOptionEqualToValue={(option, value) => {
          if (value && typeof value === 'object' && 'value' in value) {
            return option.value === value.value;
          }
          if (typeof value === 'string') {
            return option.value === value;
          }
          return false;
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <FlagOption key={key} option={option} optionProps={optionProps} />
          );
        }}
        placeholder={
          isLoadError ? t('errors:error_refresh') : t('common:select_country')
        }
        loading={isLoading}
        loadingText={t('common:loading') + '...'}
        error={!!error}
        className="Autocomplete-rounded"
        // @ts-expect-error - inputProps is a valid MUI Autocomplete prop but not exposed in CustomAutocompleteProps type
        inputProps={{
          'data-testid': 'otp-register-country-select-input',
        }}
        slotProps={{
          popper: { sx: { paddingY: 2 } },
        }}
      />
      {error && (
        <Typography
          variant="caption"
          component="p"
          color={palette.error.main}
          marginTop={1.5}
        >
          {error.message}
        </Typography>
      )}
    </Box>
  );
};
